import { identity, pipe } from 'fp-ts/function';
import * as Http from '../http/HttpClient';
import { getInternalLocaleFromISO6391Locale, InternalLocale } from '../Locale';
import { isResponseErrorOfStatus, NOT_MODIFIED } from '../http';
/**
 * The theme of the assets to be fetched.
 *
 * The 'nojquery' value indicates that the returned assets,
 * including HTML code, do not contain jQuery.
 * This is a change from previous service versions,
 * which included jQuery in the asset code.
 */
export var AssetsTheme;
(function (AssetsTheme) {
    AssetsTheme["BUYER"] = "buyer_nojquery";
    AssetsTheme["FOCUSED"] = "focused_nojquery";
    AssetsTheme["SELLER"] = "seller_nojquery";
    AssetsTheme["SELLER_ONLY"] = "seller_only";
    AssetsTheme["BUYER_ONLY"] = "buyer_only";
})(AssetsTheme || (AssetsTheme = {}));
const EMPTY_ASSET = {
    header: '',
    footer: '',
    tags: {
        css: '',
        js: '',
        head: '',
    },
    third_party: {
        gtm: '',
    },
};
const ONE_MINUTE = 60_000;
const cache = {};
/**
 * @ignore
 */
function fetchFromApi(url, options) {
    const { lang, host, cookies, timeoutMs, etag, app, authToken } = options;
    return pipe(Http.get(url), Http.setSearch({ locale: lang }), cookies ? Http.addHeader('Cookie', cookies) : identity, timeoutMs ? Http.setTimeoutMs(timeoutMs) : identity, etag ? Http.addHeader('If-None-Match', etag) : identity, Http.setServerHeaders({
        host,
        app,
        authToken,
    }), Http.send(true));
}
/**
 * @ignore
 */
const getInternalCatawikiLang = (locale) => getInternalLocaleFromISO6391Locale(locale) || InternalLocale.ENGLISH;
/**
 * @ignore
 */
function getCacheData(key) {
    const data = cache[key];
    if (!data)
        return null;
    return data;
}
/**
 * @ignore
 */
function setCacheData(key, cacheValue) {
    cache[key] = cacheValue;
}
/**
 * Fetches the assets for a given theme and locale. This function is intended to be used **only** on the server side.
 *
 * Assets are always cached for 1 minute to reduce load on cw-assets API.
 *
 * If the response status is 200, it means that the assets have changed, cache will be updated with new assets and ETag will be saved.
 * If the response status is 304, it means that the assets have not changed, it will fall into catch block and we return the cached data or empty response.
 * This logic allows us to always have fresh assets.
 *
 * @example
 * ```ts
 * import { fetchSharedHTMLAssetsOnServerSide, AssetsTheme } from '@catawiki/runtime-toolkit';
 * import { InternalLocale } from '@catawiki/runtime-toolkit';
 *
 * const assets = await fetchSharedHTMLAssetsOnServerSide('api.example.com', AssetsTheme.BUYER, {
 *  locale: InternalLocale.ENGLISH,
 *  cookies: 'test-cookie',
 *  timeoutMs: 5000,
 *  userAgent: 'test-user-agent',
 * });
 *
 * assert.strictEqual(assets.header, '<header>...</header>');
 * assert.strictEqual(assets.footer, '<footer>...</footer>');
 * assert.strictEqual(assets.tags.css, '<link rel="stylesheet" href="...">');
 * assert.strictEqual(assets.tags.js, '<script src="..."></script>');
 * assert.strictEqual(assets.tags.head, '<meta ...>');
 * assert.strictEqual(assets.third_party.gtm, '<script>...</script>');
 * ```
 * @param baseApiDomain - The base domain of the API.
 * @param theme - The theme of the assets to be fetched.
 * @param options - The options for fetching the assets.
 * @returns The assets for the given theme and locale.
 */
export async function fetchSharedHTMLAssetsOnServerSide(baseApiDomain, theme, options) {
    const { locale = InternalLocale.ENGLISH, ...apiOptions } = options;
    const key = `${locale}.${theme}`;
    const cachedData = getCacheData(key);
    if (cachedData && Date.now() < cachedData.expireAt) {
        return cachedData.assets;
    }
    const lang = getInternalCatawikiLang(locale);
    const url = `http://${baseApiDomain}/shared-assets/api/v1/components/${theme}`;
    try {
        const response = await fetchFromApi(url, {
            ...apiOptions,
            etag: cachedData?.etag || null,
            lang,
        });
        const { parsedResponse: { data }, headers, } = response;
        const etag = headers.get('etag');
        const expireAt = Date.now() + ONE_MINUTE;
        setCacheData(key, { assets: data, etag, expireAt });
        return data;
    }
    catch (error) {
        /**
         * assets have not changed, return cached data and update expireAt
         */
        if (cachedData && isResponseErrorOfStatus(error, NOT_MODIFIED)) {
            const expireAt = Date.now() + ONE_MINUTE;
            cachedData.expireAt = expireAt;
        }
        return cachedData?.assets || EMPTY_ASSET;
    }
}
